import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { platformApi } from '../../api';
import { googleOAuthApi } from '../../api/googleOAuthApi';
import { toast } from 'sonner';
import { Building2, CheckCircle, Loader2, MapPin, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

function providerErrorMessage(error, description) {
  if (description) return description;
  const messages = {
    access_denied: 'Google authorization was cancelled. No CredBloom access was granted.',
    temporarily_unavailable: 'Google authorization is temporarily unavailable. Please retry in a moment.',
    invalid_scope: 'The requested Google Business permission is not available for this OAuth application.',
  };
  return messages[error] || `Authorization failed: ${error}`;
}

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing authorization...');
  const [platform, setPlatform] = useState('');
  const [locations, setLocations] = useState([]);
  const [selecting, setSelecting] = useState(null);
  const [branchId, setBranchId] = useState('');

  const clearOAuthStorage = () => {
    localStorage.removeItem('oauth_platform');
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_branch_id');
  };

  const finishSuccess = (successMessage) => {
    clearOAuthStorage();
    setStatus('success');
    setMessage(successMessage);
    toast.success(successMessage);
    setTimeout(() => navigate('/admin/platforms'), 1800);
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const providerError = searchParams.get('error');
      const providerDescription = searchParams.get('error_description');
      const storedPlatform = localStorage.getItem('oauth_platform') || '';
      const storedState = localStorage.getItem('oauth_state') || '';
      const storedBranch = localStorage.getItem('oauth_branch_id') || '';

      setPlatform(storedPlatform);
      setBranchId(storedBranch);

      if (providerError) {
        clearOAuthStorage();
        setStatus('error');
        setMessage(providerErrorMessage(providerError, providerDescription));
        return;
      }
      if (!code || !state) {
        setStatus('error');
        setMessage('Google did not return the required authorization code. Start the connection again.');
        return;
      }
      if (!storedState || state !== storedState) {
        clearOAuthStorage();
        setStatus('error');
        setMessage('OAuth security validation failed. Start the connection again from Platforms.');
        return;
      }

      try {
        setMessage(`Connecting ${storedPlatform === 'google' ? 'Google Business Profile' : storedPlatform || 'platform'}...`);
        let response;
        if (storedPlatform === 'google') {
          response = await googleOAuthApi.handleCallback({ code, state });
        } else {
          response = await platformApi.handleOAuthCallback({ code, state });
        }
        if (cancelled) return;

        localStorage.removeItem('oauth_state');

        if (storedPlatform === 'google' && response.data.requires_location_selection) {
          setLocations(response.data.locations || []);
          setStatus('selecting');
          setMessage('Choose the Google Business Profile that belongs to this CredBloom branch.');
          return;
        }

        finishSuccess(response.data.message || `${response.data.platform || storedPlatform || 'Platform'} connected successfully`);
      } catch (error) {
        if (cancelled) return;
        console.error('OAuth callback error:', error);
        clearOAuthStorage();
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Failed to complete authorization. Start again from Platforms.');
      }
    };

    run();
    return () => { cancelled = true; };
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectBusiness = async (location) => {
    setSelecting(location.location_name);
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data } = await googleOAuthApi.selectLocation({
        account_name: location.account_name,
        location_name: location.location_name,
      }, params);
      finishSuccess(data.message || `${location.title} connected successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select this Google Business Profile');
      setSelecting(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className={`w-full ${status === 'selecting' ? 'max-w-2xl' : 'max-w-md'}`}>
        <CardContent className="space-y-5 pb-8 pt-8">
          {status === 'processing' && (
            <div className="space-y-4 text-center">
              <Loader2 size={48} className="mx-auto animate-spin text-primary" />
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>Connecting {platform === 'google' ? 'Google Business Profile' : platform || 'Platform'}</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <p className="text-xs text-muted-foreground">Do not close this page while CredBloom verifies the authorization.</p>
            </div>
          )}

          {status === 'selecting' && (
            <div className="space-y-5">
              <div className="text-center">
                <Building2 size={44} className="mx-auto text-primary" />
                <h2 className="mt-3 text-xl font-semibold" style={{ fontFamily: 'Manrope' }}>Select your Business Profile</h2>
                <p className="mt-1 text-sm text-muted-foreground">{message}</p>
              </div>
              <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
                {locations.map((location) => (
                  <button
                    type="button"
                    key={`${location.account_name}-${location.location_name}`}
                    onClick={() => selectBusiness(location)}
                    disabled={Boolean(selecting)}
                    className="w-full rounded-xl border p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:cursor-wait disabled:opacity-60"
                    data-testid={`google-location-${location.location_name}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/30"><MapPin size={20} /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-semibold text-foreground">{location.title}</p>
                          {selecting === location.location_name && <Loader2 size={16} className="shrink-0 animate-spin text-primary" />}
                        </div>
                        {location.address && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{location.address}</p>}
                        <p className="mt-1 text-[11px] text-muted-foreground">Google account: {location.account_display_name}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={() => { clearOAuthStorage(); navigate('/admin/platforms'); }}>Cancel and return to Platforms</Button>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4 text-center">
              <CheckCircle size={48} className="mx-auto text-emerald-500" />
              <h2 className="text-xl font-semibold text-emerald-600" style={{ fontFamily: 'Manrope' }}>Connection Successful</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
              <p className="text-xs text-muted-foreground">Redirecting to Platform Connections...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4 text-center">
              <XCircle size={48} className="mx-auto text-red-500" />
              <h2 className="text-xl font-semibold text-red-600" style={{ fontFamily: 'Manrope' }}>Connection Failed</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">{message}</p>
              <Button onClick={() => navigate('/admin/platforms')} className="mt-2" data-testid="back-to-platforms-btn">Back to Platforms</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
